import { useEffect, useMemo, useRef } from "react";

import { TranscriptMessageBlock } from "@/components/RequestDisplay/ChatView/TranscriptMessageBlock";
import {
  buildTraceTranscriptBlocks,
  getTraceConversationMessages,
} from "@/components/RequestDisplay/ChatView/utils";
import { DecisionAnswersResponse } from "@/components/RequestDisplay/DecisionAnswersResponse";
import { getChatRequestDisplayState } from "@/components/RequestDisplay/RequestDisplayUtils";
import { Card } from "@/components/ui/card";
import { AssistantMessage, ChatTemplate, DecisionAnswer } from "@/types";

interface RequestDisplayChatViewProps {
  chatTemplate: ChatTemplate;
  responseMessageOverride?: AssistantMessage | null;
  decisionAnswers?: DecisionAnswer[];
}

export const RequestDisplayChatView = ({
  chatTemplate,
  responseMessageOverride,
  decisionAnswers,
}: RequestDisplayChatViewProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const allMessages = useMemo(
    () => getChatRequestDisplayState(chatTemplate, responseMessageOverride).allMessages,
    [chatTemplate, responseMessageOverride],
  );
  const transcriptMessages = useMemo(() => {
    const messages = getTraceConversationMessages(allMessages);
    // Decision answers replace the assistant message that carries them as JSON text.
    return decisionAnswers?.length && messages.at(-1)?.role === "assistant"
      ? messages.slice(0, -1)
      : messages;
  }, [allMessages, decisionAnswers]);

  const transcriptBlocks = useMemo(
    () => buildTraceTranscriptBlocks(transcriptMessages),
    [transcriptMessages],
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    container.scrollTop = container.scrollHeight;
  }, [transcriptBlocks]);

  if (transcriptBlocks.length === 0) {
    return (
      <Card className="flex h-full items-center justify-center rounded-xl border border-gray-200 bg-white px-6 py-12 text-sm text-gray-500">
        No conversation messages found for this request.
      </Card>
    );
  }

  return (
    <Card className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-5">
        <div className="flex flex-col gap-y-4">
          {transcriptBlocks.map(block => (
            <TranscriptMessageBlock key={block.id} block={block} />
          ))}
          {decisionAnswers?.length ? <DecisionAnswersResponse answers={decisionAnswers} /> : null}
        </div>
      </div>
    </Card>
  );
};
